/** Passkeys verwalten (MP-E) – in Konto (Verwaltung/Anbieter) und im Mieterportal unter Sicherheit. */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Notice, StatusBadge } from "../ui";
import { Card } from "../ui/Layout";
import { api, ApiFehler, rufe } from "../api/client";
import { formatDatumZeit } from "@shared/zeit";
import { passkeyRegistrieren, passkeysMoeglich } from "../api/passkey";

interface Passkey {
  id: string;
  name: string;
  erstelltAt: string;
  zuletztAt: string | null;
}

export function PasskeyKarte({ duzen = false }: { duzen?: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["konto", "passkeys"], queryFn: () => rufe<{ passkeys: Passkey[]; rpId: string }>(api.api.konto.passkeys.$get()) });
  const nach = () => void qc.invalidateQueries({ queryKey: ["konto"] });
  const loeschen = useMutation({ mutationFn: (id: string) => rufe(api.api.konto.passkeys[":id"].$delete({ param: { id } })), onSuccess: nach });
  const [meldung, setMeldung] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const moeglich = passkeysMoeglich();

  async function anlegen() {
    setLaeuft(true);
    setMeldung(null);
    try {
      const r = await passkeyRegistrieren();
      setMeldung({ tone: "success", text: `Passkey „${r.name}“ angelegt. ${duzen ? "Beim nächsten Mal meldest du dich damit an – ohne E-Mail-Link." : "Die Anmeldung geht ab jetzt auch ohne E-Mail-Link."}` });
      nach();
    } catch (e) {
      const text = e instanceof ApiFehler ? e.message : e instanceof DOMException && e.name === "NotAllowedError" ? "Abgebrochen oder nicht erlaubt." : e instanceof DOMException && e.name === "InvalidStateError" ? "Dieses Gerät hat schon einen Passkey für dieses Konto." : e instanceof Error ? e.message : "Das hat nicht geklappt.";
      setMeldung({ tone: "error", text });
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <Card title="Passkeys" actions={moeglich ? <Button variant="secondary" icon="plus" loading={laeuft} onClick={() => void anlegen()}>Passkey anlegen</Button> : undefined}>
      <p className="sc-muted sc-small">{duzen ? "Mit Face ID, Fingerabdruck oder Geräte-PIN anmelden – ohne auf eine E-Mail zu warten. Der Passkey bleibt auf deinem Gerät oder in deinem Passwortmanager." : "Anmeldung mit Face ID, Fingerabdruck oder Geräte-PIN statt E-Mail-Link. Ein Passkey mit Nutzerprüfung zählt als zweiter Faktor."}</p>
      {!moeglich ? <Notice tone="info">Dieser Browser unterstützt keine Passkeys.</Notice> : null}
      {meldung ? <Notice tone={meldung.tone}>{meldung.text}</Notice> : null}
      {q.data?.passkeys.length ? (
        <div className="sc-col" style={{ gap: 4 }}>
          {q.data.passkeys.map((p) => (
            <div key={p.id} className="sc-spread" style={{ padding: "6px 0", borderBottom: "1px solid var(--line)" }}>
              <span className="sc-col" style={{ gap: 0 }}>
                <span className="sc-strong">{p.name}</span>
                <span className="sc-muted sc-small">Angelegt {formatDatumZeit(p.erstelltAt)}{p.zuletztAt ? ` · zuletzt ${formatDatumZeit(p.zuletztAt)}` : " · noch nicht benutzt"}</span>
              </span>
              <Button variant="quiet" icon="trash-2" loading={loeschen.isPending && loeschen.variables === p.id} onClick={() => confirm(`Passkey „${p.name}“ entfernen?`) && loeschen.mutate(p.id)}>Entfernen</Button>
            </div>
          ))}
        </div>
      ) : q.data ? (
        <p className="sc-muted sc-small" style={{ margin: 0 }}><StatusBadge status="neutral">Noch kein Passkey</StatusBadge></p>
      ) : null}
    </Card>
  );
}
